// Auto-resolve for duplicate AST scans (reconcileStatus DUPLICATE_RECORD).
// Pure functions only: no state, no API calls. See
// docs/reconciliation-cockpit-duplicate-auto.md for the business rule and why.
//
// Business rule (Rishabh, 2026-10-16). Every scan of one asset code forms a group.
//   1. The scan CLOSEST to the register place keeps the code. "Register place" is
//      the register location / sub-location AS ON the scan date
//      (registerLocationOnDate / registerClientOnDate, computed by the backend).
//      - same location AND sub-location as the register -> IGNORE (nothing to do)
//      - otherwise                                     -> ATA_ARN (transfer the
//        asset to where this scan found it)
//   2. Every other scan is a different physical item wearing a code that already
//      exists -> NEW_ASSET, with owner and invoice value copied from the asset the
//      code points to (explicitly approved inference).
//   3. Equally close scans: the EARLIEST scan keeps the code. A sub-location only
//      counts inside the right location, so all scans at a wrong location are
//      equally close.
//   4. A scan at exactly the same location + sub-location as a scan already
//      resolved is the same item scanned again -> IGNORE, no new asset.
//
// Groups are skipped (left for manual review, with the reason) rather than
// guessed when: any scan in the group already has a decision or was processed,
// only one scan of the code is loaded, the code is not in the register, or the
// tie cannot be broken because scan times are missing or identical.

const idOf = (v) => {
  if (!v) return null
  if (typeof v === 'object') return v._id ? String(v._id) : null
  return String(v)
}

// Where the scan found it. Location and sub-location are compared by id only.
export const placeKeyOf = (r) => `${idOf(r.location) || ''}|${idOf(r.client) || ''}`

const timeOf = (r) => {
  const t = r && r.time ? new Date(r.time).getTime() : NaN
  return Number.isFinite(t) ? t : null
}

// How close one scan is to the register place as on its scan date.
// A sub-location is a subset of its location, so it only counts when the
// location matches (Rishabh, 2026-10-16): a matching sub-location under a
// different location is no closer than any other wrong place.
//   2 = location and sub-location match   1 = location matches only   0 = location differs
// A sub-location matches when both are set and equal, or neither side has one
// (the backend's own status rule).
export const closenessOf = (r) => {
  const scanLoc = idOf(r.location)
  const regLoc = idOf(r.registerLocationOnDate)
  const scanSub = idOf(r.client)
  const regSub = idOf(r.registerClientOnDate)
  const loc = !!(scanLoc && regLoc && scanLoc === regLoc)
  const sub = loc && ((!scanSub && !regSub) || !!(scanSub && regSub && scanSub === regSub))
  return { loc, sub, score: loc ? (sub ? 2 : 1) : 0 }
}

const closenessLabel = ({ loc, sub }) => {
  if (loc && sub) return 'location and sub-location match the register'
  if (loc) return 'location matches the register, sub-location differs'
  return 'location differs from the register'
}

// Owner and invoice value for a NEW_ASSET, copied from the asset the scanned code
// points to. Only values that exist are copied; anything absent stays unset so
// Process blocks the record and asks for it.
export const copiedDetailsFor = (r) => {
  const m = (r && r.matchedAsset) || {}
  const fields = {}
  const owner = idOf(m.owner)
  if (owner) fields.ownedBy = owner
  const rate = m.invoiceValue == null || m.invoiceValue === '' ? NaN : Number(m.invoiceValue)
  if (Number.isFinite(rate)) fields.rate = rate
  return fields
}

/**
 * Plan the resolution of every duplicate group in astData.
 *
 * @param astData   the AST rows as held by the cockpit (each has a unique _uid)
 * @param hasDecision (r) => true when the row already has a decision of any kind
 *                  (decided, pending clear, processed, in flight or failed)
 * @returns { groups: [{ code, rows: [{ r, action, why, details? }] }],
 *            skipped: [{ code, rows, reason }] }
 */
export const planDuplicates = (astData, hasDecision) => {
  const byCode = new Map()
  ;(astData || []).forEach(r => {
    if (!r || r.reconcileStatus !== 'DUPLICATE_RECORD' || !r.assetCode) return
    if (!byCode.has(r.assetCode)) byCode.set(r.assetCode, [])
    byCode.get(r.assetCode).push(r)
  })

  const groups = []
  const skipped = []
  byCode.forEach((rows, code) => {
    const skip = (reason) => skipped.push({ code, rows, reason })

    if (rows.every(hasDecision)) return // already fully decided - nothing to do
    if (rows.length < 2) return skip('only one scan of this code is loaded')
    if (rows.some(hasDecision)) return skip('part of this group already has a decision')
    if (rows.some(r => !r.matchedAsset || !r.matchedAsset._id)) return skip('the code is not in the register')
    if (rows.some(r => timeOf(r) === null)) return skip('a scan has no scan time, so the earliest cannot be told')
    // Closeness needs both places. A blank register location on the scan date means
    // the register had the asset in transit (an open transfer never received), and a
    // scan with no location cannot be compared; neither is "location differs".
    if (rows.some(r => !idOf(r.registerLocationOnDate))) {
      return skip('the register shows no location on the scan date (asset in transit) - decide by hand')
    }
    if (rows.some(r => !idOf(r.location))) return skip('a scan has no location - decide by hand')

    const ranked = rows
      .map(r => ({ r, c: closenessOf(r), t: timeOf(r) }))
      .sort((a, b) => (b.c.score - a.c.score) || (a.t - b.t))
    const top = ranked[0]
    const tiedAtTop = ranked.filter(x => x.c.score === top.c.score)
    // Same score, same instant, different places: nothing left to choose by.
    if (tiedAtTop.some(x => x !== top && x.t === top.t && placeKeyOf(x.r) !== placeKeyOf(top.r))) {
      return skip('two equally close scans at different places share the same scan time')
    }

    const out = []
    const exact = top.c.loc && top.c.sub
    // Only a tie between DIFFERENT places is a choice worth naming.
    const rivals = tiedAtTop.filter(x => placeKeyOf(x.r) !== placeKeyOf(top.r)).length
    const tieNote = rivals ? ` — earliest of ${rivals + 1} equally close scans at different places` : ''
    out.push({
      r: top.r,
      action: exact ? 'IGNORE' : 'ATA_ARN',
      refRecordId: exact ? null : top.r.matchedAsset._id,
      why: exact
        ? `Keeps the code: ${closenessLabel(top.c)}${tieNote}. Nothing to move.`
        : `Keeps the code: ${closenessLabel(top.c)}${tieNote}. Transfer to where it was scanned.`
    })

    // Places already accounted for: the keeper's, then each new asset's.
    const resolvedPlaces = new Map([[placeKeyOf(top.r), top.r]])
    ranked.slice(1)
      .sort((a, b) => a.t - b.t) // earliest first, so the first scan at a place is the one that becomes the asset
      .forEach(({ r }) => {
        const key = placeKeyOf(r)
        if (resolvedPlaces.has(key)) {
          const first = resolvedPlaces.get(key)
          out.push({
            r, action: 'IGNORE',
            why: first === top.r
              ? 'Same location and sub-location as the scan that keeps the code — the same item scanned again.'
              : 'Same location and sub-location as another scan of this code — the same item scanned again.'
          })
          return
        }
        resolvedPlaces.set(key, r)
        const details = copiedDetailsFor(r)
        const copied = [details.ownedBy && 'owner', details.rate !== undefined && 'invoice value'].filter(Boolean)
        const gaps = [!details.ownedBy && 'owner', details.rate === undefined && 'invoice value'].filter(Boolean)
        out.push({
          r, action: 'NEW_ASSET', details,
          why: 'A different item at another place — new asset.' +
            (copied.length ? ` ${copied.join(' and ')} copied from ${code}.`.replace(/^ (\w)/, (m, c) => ' ' + c.toUpperCase()) : '') +
            (gaps.length ? ` ${code} has no ${gaps.join(' or ')} — enter it before Process.` : '')
        })
      })

    groups.push({ code, rows: out })
  })

  groups.sort((a, b) => a.code.localeCompare(b.code))
  skipped.sort((a, b) => a.code.localeCompare(b.code))
  return { groups, skipped }
}
